import { useTranslation } from 'react-i18next';
import {
  RecommendationStockGroup,
  RecommendationStockScope,
  type FrameRecommendationStock,
} from '@optiflow/types';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';
import { formatList } from '@/utils/format';

/**
 * Where a frame can be had, in words that claim no more than the server looked at.
 *
 * Rendered only when the server sent stock. With the current branch alone in scope, "nothing here" is all that is known, so it never says out of stock; with every authorized branch in scope it speaks of the branches this user can see, and names only the branches the server returned.
 */
export function StockAvailability({
  stock,
  scope,
}: {
  stock: FrameRecommendationStock;
  scope: RecommendationStockScope;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const names = stock.otherAuthorizedBranches.map((branch) => branch.branchName);
  const branchList = names.length > 0 ? formatList(names, locale) : undefined;

  const line = (tone: 'available' | 'elsewhere' | 'none', text: string, detail?: string) => (
    <p className="flex items-start gap-2 text-sm">
      <span
        aria-hidden
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          tone === 'available' && 'bg-emerald-500',
          tone === 'elsewhere' && 'bg-amber-500',
          tone === 'none' && 'bg-line',
        )}
      />
      <span className="min-w-0">
        <span className={tone === 'none' ? 'text-muted' : 'text-ink'}>{text}</span>
        {detail ? <span className="text-muted block text-xs">{detail}</span> : null}
      </span>
    </p>
  );

  switch (stock.group) {
    case RecommendationStockGroup.CURRENT_BRANCH:
      return line(
        'available',
        t('frameRecommendations.stock.inThisBranch', { quantity: stock.currentBranchQuantity }),
        scope === RecommendationStockScope.AUTHORIZED_BRANCHES && branchList
          ? t('frameRecommendations.stock.alsoAt', { branches: branchList })
          : undefined,
      );
    case RecommendationStockGroup.OTHER_AUTHORIZED_BRANCH:
      return line(
        'elsewhere',
        t('frameRecommendations.stock.atOtherBranch'),
        branchList ? t('frameRecommendations.stock.at', { branches: branchList }) : undefined,
      );
    case RecommendationStockGroup.OUT_OF_STOCK:
      return line(
        'none',
        scope === RecommendationStockScope.AUTHORIZED_BRANCHES
          ? t('frameRecommendations.stock.notInVisibleBranches')
          : t('frameRecommendations.stock.notInThisBranch'),
      );
  }
}
